export enum answerStatsTypes {
  bubbles,
  barChart,
  horizontalBar,
  wordCloud,
}

export interface Comment {
  id: string;
  text: string;
  from: string;
}

export type StatConfig =
  | BubbleStatsConfig
  | BarStatsConfig
  | BarChartStatsConfig
  | WordCloudStatsConfig;
export type StatValues =
  | BubbleStatsValues
  | BarStatsValues
  | BarChartStatsValues
  | WordCloudStatsValues;
export type Stat = BubbleStats | BarStats | BarChartStats | WordCloudStats;

export interface AnswerStat {
  stats: Stat;
  comments: Array<Comment>;
  amountNotAnswered: number;
  amountCorrect: number;
  amountIncorrect: number;
  amountReadyForNextQuestion: number;
}

export type AnswerStats = Record<string, AnswerStat>;

export interface LabelAndColor {
  label: string;
  brightColor: string;
  mainColor: string;
  darkColor: string;
}

export interface BubbleStatsConfig {
  title: string;
  type: answerStatsTypes.bubbles;
  labelAndColorForVal1: LabelAndColor;
  labelAndColorForVal2: LabelAndColor;
  labelAndColorForVal3: LabelAndColor;
}

export interface BarStatsConfig {
  title: string;
  type: answerStatsTypes.horizontalBar;
  labelAndColorForBar1: LabelAndColor;
  labelAndColorForBar2: LabelAndColor;
  labelAndColorForBar3: LabelAndColor;
  labelAndColorForBar4?: LabelAndColor;
  labelAndColorForBar5?: LabelAndColor;
}

export interface BarChartStatsConfig {
  title: string;
  type: answerStatsTypes.barChart;
  labelAndColorForBar1: LabelAndColor;
  labelAndColorForBar2: LabelAndColor;
  labelAndColorForBar3: LabelAndColor;
  labelAndColorForBar4?: LabelAndColor;
  labelAndColorForBar5?: LabelAndColor;
}

export interface WordCloudStatsConfig {
  textColor: string;
  type: answerStatsTypes.wordCloud;
}

export interface BubbleStatsValues {
  val1: number;
  val2: number;
  val3: number;
}

export interface BarStatsValues {
  bar1: number;
  bar2: number;
  bar3: number;
  bar4?: number;
  bar5?: number;
}

export interface BarChartStatsValues {
  bar1: number;
  bar2: number;
  bar3: number;
  bar4?: number;
  bar5?: number;
}

export interface WordCloudStatsValues {
  comments: Array<Pick<Comment, "from" | "text">>;
}

export interface BubbleStats {
  type: answerStatsTypes.bubbles;
  values: BubbleStatsValues;
  config: BubbleStatsConfig;
}

export interface BarStats {
  type: answerStatsTypes.horizontalBar;
  values: BarStatsValues;
  config: BarStatsConfig;
}

export interface BarChartStats {
  type: answerStatsTypes.barChart;
  values: BarChartStatsValues;
  config: BarChartStatsConfig;
}

export interface WordCloudStats {
  type: answerStatsTypes.wordCloud;
  values: WordCloudStatsValues;
  config: WordCloudStatsConfig;
}

export const isLabelAndColor = (val: unknown): val is LabelAndColor => {
  return (val as LabelAndColor).mainColor !== undefined;
};

export const isBarStatsValues = (val: unknown): val is BarStatsValues => {
  return (val as BarStatsValues).bar1 !== undefined;
};

export const isBubbleStatsValues = (val: unknown): val is BubbleStatsValues => {
  return (val as BubbleStatsValues).val3 !== undefined;
};

export const isBarChartStatsValues = (val: unknown): val is BarChartStatsValues => {
  return (val as BarChartStatsValues).bar1 !== undefined;
};

export const isWordCloudStatValues = (val: unknown): val is WordCloudStatsValues => {
  return (val as WordCloudStatsValues).comments !== undefined;
};
